import * as THREE from "three";
import React, { useEffect, useRef, useState } from "react";
import { Line2 } from "three/examples/jsm/lines/Line2";
import { LineMaterial } from "three/examples/jsm/lines/LineMaterial.js";
import { LineGeometry } from "three/examples/jsm/lines/LineGeometry.js";
import { OBJLoader2 } from "wwobjloader2";
import { measurements, MeasurePoint } from "./measurePoints";
import { useThree } from "@react-three/fiber";
import { Mesh, MeshPhongMaterial } from "three";
import { OrbitControls } from "@react-three/drei";
import { ObjectModel } from "../common/types";
import ConvexHullGrahamScan from "./graham";
import { getMeasurementFromModel } from "./measure/measureCache";

interface MeasurePoints3D {
  name: string;
  points: THREE.Vector3[];
}

export interface ModelsHolder {
  models: ObjectModel[];
  activeModel: number;
}

interface ObjectModelProps {
  models: ModelsHolder;
  showMeasurement: string;
  activeModel?: number;
}

// —————————————————————————————————————————————————————————————————————————
// Helper: get a Vector3 from a vertex index “v” on a mesh’s position attribute
// —————————————————————————————————————————————————————————————————————————
function getVertexPosition(v: number, mesh: THREE.Mesh): THREE.Vector3 {
  const posAttr = mesh.geometry.attributes.position;
  const stride = posAttr.itemSize; // typically 3 (x, y, z)
  const i = v * stride;
  const array = posAttr.array as number[];
  return new THREE.Vector3(array[i], array[i + 1], array[i + 2]);
}

// —————————————————————————————————————————————————————————————————————————
// Helper: compute a plane (normal + offset) from three vertex indices
// —————————————————————————————————————————————————————————————————————————
function planeFromPoints(
  planeIdx: number[],
  mesh: THREE.Mesh
): { normal: THREE.Vector3; offset: number } {
  const A = getVertexPosition(planeIdx[0], mesh);
  const B = getVertexPosition(planeIdx[1], mesh);
  const C = getVertexPosition(planeIdx[2], mesh);

  const normal = new THREE.Vector3()
    .crossVectors(
      new THREE.Vector3().subVectors(A, B),
      new THREE.Vector3().subVectors(A, C)
    )
    .normalize();
  const offset = -normal.dot(A);
  return { normal, offset };
}

// —————————————————————————————————————————————————————————————————————————
// Helper: approximate a “plane normal” by averaging two heel points and
// subtracting from a head point (replicates original getPlaneNormal logic)
// —————————————————————————————————————————————————————————————————————————
function getPlaneNormal(mesh: THREE.Mesh): THREE.Vector3 {
  let avg = new THREE.Vector3();
  const pointMap = { heels: { vertices: [3466, 6867] } };
  const list = pointMap.heels.vertices;
  for (const idx of list) {
    avg.add(getVertexPosition(idx, mesh));
  }
  avg.divideScalar(list.length);

  const head = getVertexPosition(414, mesh);
  return head.sub(avg).normalize();
}

enum Direction {
  OUT = 1,
  OUT_Y = 2,
  IN_Y = 3,
  NONE = 4,
}

// —————————————————————————————————————————————————————————————————————————
// Draw a Line2 between two Vector3s, optionally offsetting “out” or “in”.
// The resulting Line2 is added into the provided THREE.Group.
// —————————————————————————————————————————————————————————————————————————
function addLine(
  pointA: THREE.Vector3,
  pointB: THREE.Vector3,
  container: THREE.Group,
  color?: number,
  direction: Direction = Direction.NONE
) {
  const mat = new LineMaterial({
    color: color ?? 0xfcf9a2,
    linewidth: 0.01,
    dashed: false,
  });
  const line = new Line2(new LineGeometry(), mat);

  let pa = pointA.clone();
  let pb = pointB.clone();
  const parentMesh = container.parent as THREE.Mesh;
  if (parentMesh) {
    const planeNormal = getPlaneNormal(parentMesh);
    if (direction === Direction.OUT) {
      const moveBy = -0.5;
      const offA = pointA
        .clone()
        .projectOnVector(planeNormal)
        .sub(pointA)
        .normalize()
        .multiplyScalar(moveBy);
      const offB = pointB
        .clone()
        .projectOnVector(planeNormal)
        .sub(pointB)
        .normalize()
        .multiplyScalar(moveBy);
      pa.add(offA);
      pb.add(offB);
    } else if (
      direction === Direction.OUT_Y ||
      direction === Direction.IN_Y
    ) {
      const moveBy = 0.02 * (direction === Direction.OUT_Y ? 1 : -1);
      const offA = pointA
        .clone()
        .projectOnVector(planeNormal)
        .sub(pointA)
        .normalize()
        .multiplyScalar(moveBy);
      const offB = pointB
        .clone()
        .projectOnVector(planeNormal)
        .sub(pointB)
        .normalize()
        .multiplyScalar(moveBy);
      pa.add(offA);
      pb.add(offB);
    }
  }

  line.geometry.setPositions([pa.x, pa.y, pa.z, pb.x, pb.y, pb.z]);
  line.computeLineDistances();
  container.add(line);
}

// —————————————————————————————————————————————————————————————————————————
// Given a MeasurePoint (with vertices, plane info, possibly convex_hull),
// return an ordered array of THREE.Vector3 points in 3D space.
// —————————————————————————————————————————————————————————————————————————
function getMeasurePoints(
  mp: MeasurePoint,
  mesh: THREE.Mesh
): THREE.Vector3[] {
  const pts: THREE.Vector3[] = [];
  if (!mp.vertices) return pts;

  let plane: { normal: THREE.Vector3; offset: number } | null = null;
  let planeOrigin: THREE.Vector3 | null = null;
  let tangentX: THREE.Vector3 | null = null;
  let tangentY: THREE.Vector3 | null = null;
  let convexHull: ConvexHullGrahamScan | null = null;

  if (mp.usePlaneNormal && mp.vertices.length > 2 && !mp.plane) {
    const N = mp.vertices.length;
    const i0 = 0;
    const i1 = Math.floor(N / 3);
    const i2 = Math.floor((2 * N) / 3);
    mp.plane = [mp.vertices[i0], mp.vertices[i1], mp.vertices[i2]];
    plane = planeFromPoints(mp.plane, mesh);
  } else if (mp.plane) {
    plane = planeFromPoints(mp.plane, mesh);
  }

  if (plane && mp.plane) {
    planeOrigin = plane.normal.clone().multiplyScalar(-plane.offset);
    if (mp.convex_hull) {
      const refPt = getVertexPosition(mp.plane[1], mesh);
      tangentX = new THREE.Vector3()
        .subVectors(refPt, planeOrigin)
        .normalize();
      tangentY = new THREE.Vector3().crossVectors(
        plane.normal,
        tangentX
      ).normalize();
      convexHull = new ConvexHullGrahamScan();
    }
  }

  for (const idx of mp.vertices) {
    const vp3 = getVertexPosition(idx, mesh);
    if (convexHull && planeOrigin && tangentX && tangentY) {
      const rel = vp3.clone().sub(planeOrigin);
      const x = rel.dot(tangentX);
      const y = rel.dot(tangentY);
      convexHull.addPoint(x, y);
    } else if (plane && planeOrigin) {
      const projected = vp3
        .clone()
        .sub(planeOrigin)
        .projectOnPlane(plane.normal)
        .add(planeOrigin);
      pts.push(projected);
    } else {
      pts.push(vp3);
    }
  }

  if (convexHull && planeOrigin && tangentX && tangentY) {
    const hullPts2D = convexHull.getHull();
    hullPts2D.push(hullPts2D[0]);
    for (const h of hullPts2D) {
      const x = h.x;
      const y = h.y;
      const worldPt = planeOrigin
        .clone()
        .add(tangentX.clone().multiplyScalar(x))
        .add(tangentY.clone().multiplyScalar(y));
      pts.push(worldPt);
    }
  }

  return pts;
}

// —————————————————————————————————————————————————————————————————————————
// The ThreeDModel component: renders a single scene, swaps OBJ on prop change,
// and draws measurement lines via visualByName.
// Uses a “cancellation flag” so that only the latest load callback adds a mesh.
// —————————————————————————————————————————————————————————————————————————
const ThreeDModel: React.FC<ObjectModelProps> = ({
  models,
  showMeasurement,
  activeModel = 0,
}) => {
  // 1) Memoize one Scene & one Material for the component’s lifetime
  const sceneRef = useRef<THREE.Scene>(new THREE.Scene());
  const materialRef = useRef<MeshPhongMaterial>(
    new MeshPhongMaterial({
      color: 0xffffff,
      wireframe: false,
      flatShading: false,
      opacity: 0.4,
    })
  );

  // 2) Refs for the currently displayed Object3D, its main Mesh, and the Group for lines
  const objRef = useRef<THREE.Object3D | null>(null);
  const meshRef = useRef<THREE.Mesh | null>(null);
  const visualRef = useRef<THREE.Group>(new THREE.Group());

  // 3) State: once we load a Mesh, we compute all measurement point sets and store them here
  const [measurePoints, setMeasurePoints] = useState<MeasurePoints3D[]>(
    []
  );

  // 4) Grab R3F’s camera from context (so we can attach a light and add the camera to our scene)
  const camera = useThree((state) => state.camera);

  // 5) Track a “load version” so that stale loader callbacks do nothing
  const loadVersionRef = useRef(0);

  // —————————————————————————————————————————————————————————————————————
  // Effect #1: On first mount (or if camera changes), attach a PointLight under the camera
  // and add the camera to our scene. StrictMode may run this twice, but we remove
  // any old lights each time so we don’t stack them.
  // —————————————————————————————————————————————————————————————————————
  useEffect(() => {
    camera.traverse((child) => {
      if (child instanceof THREE.PointLight) {
        camera.remove(child);
      }
    });
    const light = new THREE.PointLight(0xffffff, 0.35);
    camera.add(light);
    sceneRef.current.add(camera);
  }, [camera]);

  // —————————————————————————————————————————————————————————————————————
  // Effect #2: Whenever `models.models` OR `activeModel` changes:
  //  • Increment loadVersionRef, store in local `myVersion`.
  //  • Remove old objRef from scene & dispose.
  //  • Start loader.load; inside callback, check if myVersion === current loadVersionRef.
  //    If not, dispose the newly loaded object and return early.
  // —————————————————————————————————————————————————————————————————————
  useEffect(() => {
    // Increment version so previous callbacks skip
    loadVersionRef.current += 1;
    const myVersion = loadVersionRef.current;

    // 2a) Remove & dispose old object
    if (objRef.current) {
      sceneRef.current.remove(objRef.current);
      objRef.current.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.geometry.dispose();
          if (Array.isArray(child.material)) {
            child.material.forEach((mat) => mat.dispose());
          } else {
            child.material.dispose();
          }
        }
      });
      visualRef.current.clear();
      objRef.current = null;
      meshRef.current = null;
      setMeasurePoints([]);
    }

    // 2b) Grab the new model (if it exists)
    const thisModel = models.models[activeModel];
    if (!thisModel) {
      console.warn(
        "ThreeDModel: no model at index",
        activeModel
      );
      return;
    }

    // 2c) Load the OBJ asynchronously
    const loader = new OBJLoader2();
    loader.setUseIndices(true);
    loader.load(
      "data:model/obj;base64," + thisModel.obj,
      (loadedObject) => {
        // If a newer effect run occurred, skip this callback
        if (myVersion !== loadVersionRef.current) {
          // Dispose immediately since this is stale
          loadedObject.traverse((child) => {
            if (child instanceof THREE.Mesh) {
              child.geometry.dispose();
              if (Array.isArray(child.material)) {
                child.material.forEach((mat) => mat.dispose());
              } else {
                child.material.dispose();
              }
            }
          });
          return;
        }
      

        // Otherwise, this is the activeModel’s correct callback
        loadedObject.traverse((child) => {
           
          if (
            child instanceof THREE.Mesh || child.type === "Mesh"
          ) {
            meshRef.current = child as THREE.Mesh;
           
            meshRef.current.material = materialRef.current;
            // Compute measurement points
            Promise.all(
              measurements.map(async (mp) => {
                let pts = getMeasurePoints(
                  mp,
                  meshRef.current!
                );
                if (mp.name === "waist_female") {
                  const res = await getMeasurementFromModel({
                    obj: thisModel.obj,
                    sessionID: thisModel.id,
                  });
                  pts = res.best.slice.points;
                }
                return { name: mp.name, points: pts };
              })
            ).then((allPts) => {
              setMeasurePoints(allPts);
            });
          }
        });

        // Attach lines group and add to scene
        objRef.current = loadedObject;
        objRef.current.rotation.x = Math.PI;
        objRef.current.rotation.y = Math.PI;
        if (meshRef.current) {
          meshRef.current.add(visualRef.current);
        }
        sceneRef.current.add(objRef.current);
      }
    );
  }, [models.models, activeModel]);

  // —————————————————————————————————————————————————————————————————————
  // Draw or clear lines based on `showMeasurement`
  // —————————————————————————————————————————————————————————————————————
  const visualByName = (name: string) => {
    visualRef.current.clear();
    const mp = measurePoints.find((m) => m.name === name);
    if (!mp) return;
    for (let i = 1; i < mp.points.length; i++) {
      let dir = Direction.NONE;
      if (name === "leg_inner") dir = Direction.IN_Y;
      else if (name === "arm_outer") dir = Direction.OUT_Y;
      addLine(
        mp.points[i - 1],
        mp.points[i],
        visualRef.current,
        0x196e44,
        dir
      );
    }
  };

  // —————————————————————————————————————————————————————————————————————
  // Effect #3: whenever `measurePoints` or `showMeasurement` changes, redraw lines
  // —————————————————————————————————————————————————————————————————————
  useEffect(() => {

    visualByName(showMeasurement);
  }, [measurePoints, showMeasurement]);

  // —————————————————————————————————————————————————————————————————————
  // Render: always use the same `sceneRef.current` pointer so R3F doesn’t unmount/remount.
  // —————————————————————————————————————————————————————————————————————
  return (
    <>
      <primitive object={sceneRef.current} />
      <OrbitControls />
    </>
  );
};

export default ThreeDModel;
